import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { Button } from "primereact/button";
import { UserAccess } from "../../../hooks/useUserAccess";
import { MoveDirection, WidgetFilterValues } from "../prefs/homePrefs";
import WidgetFilterDialog from "./WidgetFilterDialog";
import { SelectOption, WidgetDefinition, getScope, getStringArray, MANAGERS_KEY } from "./types";
import { CardLoading } from "./ui";

interface Props {
  def: WidgetDefinition;
  access: UserAccess;
  filters: WidgetFilterValues;
  hasCustomFilters: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: MoveDirection) => void;
  onHide: () => void;
  onSaveFilters: (values: WidgetFilterValues | null) => void;
}

/**
 * True once the element has come near the viewport. Cards mount their body
 * (and so fetch) lazily, so cards below the fold don't hold up the ones on top.
 */
const useSeen = (ref: React.RefObject<HTMLElement>, rootMargin = "200px"): boolean => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (seen || !el) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setSeen(true);
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin, seen]);
  return seen;
};

/** Count of non-scope filters that narrow the card (for the header chip). */
const activeFilterCount = (def: WidgetDefinition, filters: WidgetFilterValues): number =>
  def.filterFields.filter((f) => {
    const v = filters[f.key];
    const d = def.defaultFilters[f.key];
    if (f.type === "multiselect") return Array.isArray(v) && v.length > 0;
    return v !== undefined && JSON.stringify(v) !== JSON.stringify(d);
  }).length;

const WidgetCard: React.FC<Props> = ({
  def,
  access,
  filters,
  hasCustomFilters,
  isFirst,
  isLast,
  onMove,
  onHide,
  onSaveFilters,
}) => {
  const menuRef = useRef<Menu>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const seen = useSeen(sectionRef);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, SelectOption[]>>({});

  const onOptions = useCallback((key: string, options: SelectOption[]) => {
    setDynamicOptions((current) =>
      JSON.stringify(current[key]) === JSON.stringify(options) ? current : { ...current, [key]: options }
    );
  }, []);

  const mineLabel = def.mineLabel ?? "My issues";
  const scope = getScope(filters);
  const scopeLabel =
    scope === "all"
      ? "Everyone"
      : scope === "managers"
        ? `${getStringArray(filters, MANAGERS_KEY).length} manager${
            getStringArray(filters, MANAGERS_KEY).length === 1 ? "" : "s"
          }`
        : mineLabel;
  const extraFilters = activeFilterCount(def, filters);
  const hasFilterUi = def.supportsScope || def.filterFields.length > 0;

  const menuItems: MenuItem[] = [
    ...(hasFilterUi
      ? [{ label: "Filters…", icon: "pi pi-sliders-h", command: () => setFiltersOpen(true) }]
      : []),
    { label: "Move up", icon: "pi pi-arrow-up", disabled: isFirst, command: () => onMove("up") },
    { label: "Move down", icon: "pi pi-arrow-down", disabled: isLast, command: () => onMove("down") },
    { label: "Move to top", icon: "pi pi-angle-double-up", disabled: isFirst, command: () => onMove("top") },
    {
      label: "Move to bottom",
      icon: "pi pi-angle-double-down",
      disabled: isLast,
      command: () => onMove("bottom"),
    },
    { separator: true },
    ...(hasCustomFilters
      ? [{ label: "Reset filters", icon: "pi pi-refresh", command: () => onSaveFilters(null) }]
      : []),
    { label: "Hide card", icon: "pi pi-eye-slash", command: onHide },
  ];

  const Body = def.Component;

  return (
    <section
      ref={sectionRef}
      className={`home-card${def.wide ? " home-card--wide" : ""}`}
      aria-labelledby={`home-card-${def.id}`}
    >
      <header className="home-card__header">
        <span className="home-card__icon" style={{ color: def.accent, background: `${def.accent}1f` }}>
          <i className={def.icon} />
        </span>
        <div className="home-card__titles">
          <Link to={def.path} className="home-card__title" id={`home-card-${def.id}`}>
            {def.title}
          </Link>
          <span className="home-card__desc">{def.description}</span>
        </div>
        <div className="home-card__actions">
          {def.supportsScope && (
            <button
              type="button"
              className={`home-chip home-chip--${scope}`}
              onClick={() => setFiltersOpen(true)}
              title="Change who this card shows"
            >
              <i className={scope === "all" ? "pi pi-globe" : scope === "managers" ? "pi pi-users" : "pi pi-user"} />
              {scopeLabel}
              {extraFilters > 0 && <span className="home-chip__count">+{extraFilters}</span>}
            </button>
          )}
          {!def.supportsScope && extraFilters > 0 && (
            <button type="button" className="home-chip" onClick={() => setFiltersOpen(true)}>
              <i className="pi pi-filter" /> {extraFilters} filter{extraFilters === 1 ? "" : "s"}
            </button>
          )}
          <Button
            icon="pi pi-ellipsis-v"
            rounded
            text
            size="small"
            aria-label={`Options for ${def.title}`}
            onClick={(e) => menuRef.current?.toggle(e)}
          />
          <Menu model={menuItems} popup ref={menuRef} />
        </div>
      </header>

      <div className="home-card__body">
        {seen ? <Body access={access} filters={filters} onOptions={onOptions} /> : <CardLoading />}
      </div>

      <footer className="home-card__footer">
        <Link to={def.path} className="home-card__open">
          Open {def.title} <i className="pi pi-arrow-right" />
        </Link>
      </footer>

      {hasFilterUi && (
        <WidgetFilterDialog
          visible={filtersOpen}
          def={def}
          filters={filters}
          dynamicOptions={dynamicOptions}
          mineLabel={mineLabel}
          onHide={() => setFiltersOpen(false)}
          onSave={(values) => {
            onSaveFilters(values);
            setFiltersOpen(false);
          }}
          onReset={() => {
            onSaveFilters(null);
            setFiltersOpen(false);
          }}
        />
      )}
    </section>
  );
};

export default WidgetCard;
